import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { MetaGamesEventType } from '../../dto/metagames.types';
import type { MetagamesCurrentEvent } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class PrizeDropStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    const prizeDropFlag = resolveFlag('prize_drop', true);
    this.setPrizeDropFlag(prizeDropFlag);

    const prizeDropPreMessageFlag = resolveFlag('prize_drop_pre_message', true);
    this.setPrizeDropPreMessageFlag(prizeDropPreMessageFlag);

    ldClient?.on('ready', () => {
      this.setPrizeDropFlag(resolveFlag('prize_drop', true));
      this.setPrizeDropPreMessageFlag(resolveFlag('prize_drop_pre_message', false));
    });

    ldClient?.on('change', (changes) => {
      if ('prize_drop' in changes) {
        this.setPrizeDropFlag(resolveFlag('prize_drop', true));
      }

      if ('prize_drop_pre_message' in changes) {
        this.setPrizeDropPreMessageFlag(resolveFlag('prize_drop_pre_message', false));
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.resolveAndUpdateFlags();
      } else {
        this.setPrizeDropFlag(false);
      }
    });
  }

  private resolveAndUpdateFlags() {
    const prizeDropFlag = resolveFlag('prize_drop', true);

    this.setPrizeDropFlag(prizeDropFlag);
  }

  @observable
  public prizeDropFlag = false;

  @action
  public setPrizeDropFlag(flag: boolean) {
    this.prizeDropFlag = flag;
  }

  @observable
  public prizeDropPreMessageFlag = false;

  @action
  public setPrizeDropPreMessageFlag(flag: boolean) {
    this.prizeDropPreMessageFlag = flag;
  }

  @computed
  public get prizeDropEventByCoin() {
    if (!this.prizeDropEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.prizeDropEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @computed
  public get currentOpenPrizeDropEventByCoin() {
    if (!this.currentOpenPrizeDropEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.currentOpenPrizeDropEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @computed
  public get isPrizeDropEnabledInScope() {
    if (!this.currentOpenPrizeDropEvent) return;
    const { currentGame } = this.rootStore.gameStore;
    const { participatingGames, participatingProviders } = this.currentOpenPrizeDropEvent.eventScopes;

    if (participatingGames.length) {
      return participatingGames.includes(Number(currentGame?.gameId));
    } else if (participatingProviders.length) {
      return participatingProviders.includes(Number(currentGame?.providerId));
    }

    return true;
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public prizeDropEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setPrizeDropEvent(event: MetagamesCurrentEvent | undefined) {
    this.prizeDropEvent = event;
  }

  @observable
  public currentOpenPrizeDropEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenPrizeDropEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenPrizeDropEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  private setAndOpenDialog(prizeInfoData: MetagamesCurrentEvent | undefined) {
    this.setCurrentOpenPrizeDropEvent(prizeInfoData);
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenMetagamesDialog,
      options: { type: MetaGamesEventType.PRIZE_DROP },
    });
  }

  public updatePrizeDropEvent(newEvent: MetagamesCurrentEvent | undefined) {
    this.setPrizeDropEvent(newEvent);

    if (newEvent) {
      this.rootStore.gameStore.sendSlotsGameUiMessage({
        type: 'newMetagamesEvent',
        payload: {
          metagamesEvent: toJS(newEvent),
        },
      });
    }
  }

  private updateEventOptedInStatus(eventId: string, coinType: CoinType, isOptedIn: boolean) {
    if (!this.prizeDropEvent) {
      devError('PrizeDropStore: updateEventOptedInStatus error: prizeDropEvent not found');
      return;
    }

    const coinTypeLower = coinType.toLowerCase() as 'gc' | 'sc';

    if (this.prizeDropEvent && this.prizeDropEvent.id === eventId && this.prizeDropEvent[coinTypeLower]) {
      const updatedCurrentEvent = {
        ...this.prizeDropEvent,
        [coinTypeLower]: {
          ...this.prizeDropEvent[coinTypeLower],
          isOptedIn,
        },
      };

      this.setPrizeDropEvent(updatedCurrentEvent);
    }
  }

  public async acknowledgeOptIn(eventId: string) {
    try {
      const { coinType } = this.rootStore.coinTypeStore;

      this.updateEventOptedInStatus(eventId, coinType, true);

      await this.rootStore.metagamesStore.optInToEvent(eventId, coinType);
    } catch (error) {
      devError('HammersStore: acknowledgeOptIn error', error);
    }
  }

  public startDialogs(type?: FeatureManagerDialogType | undefined) {
    if (type === 'PrizeDropPreMessage' && this.prizeDropPreMessageFlag) {
      const prizeDropPreMessageEvent =
        this.prizeDropEventByCoin && !this.prizeDropEventByCoin.isOptedIn ? this.prizeDropEventByCoin : undefined;

      if (prizeDropPreMessageEvent) {
        void this.startPrizeDropGame();
        return true;
      }
    }

    return false;
  }

  public async startPrizeDropGame(options?: { type?: string }) {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.prizeDropEvent) {
        devError('HammersStore: startPrizeDropEvent error: hammersEvent not found');
        return;
      }

      const eventByCoin = this.prizeDropEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('HammersStore: startPrizeDropEvent error: event not found');
        return;
      }

      const { type } = options || {};

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
        type,
      });

      if (!launchGameResponse.data?.data) {
        devError('HammersStore: startPrizeDropEvent error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (metagames) {
        const { token, path } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          metagamesToken: gameUiFlag ? token : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.prizeDropEvent,
          token,
          path,
          url: metagamesUrl,
        };

        this.setAndOpenDialog(metagamesEventToSet);
      } else {
        devError('HammersStore: startPrizeDropEvent error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('PrizeDropStore: startPrizeDropEvent error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame(dialogType?: FeatureManagerDialogType) {
    this.setCurrentOpenPrizeDropEvent(undefined);
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  public resetEvent() {
    this.setPrizeDropEvent(undefined);

    if (this.currentOpenPrizeDropEvent) {
      this.closeGame();
    }
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: 'prizeDrop',
    });
  }
}
